
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface PerformanceGoal {
  id: string;
  goal_title: string;
  goal_description: string;
  goal_category: string;
  goal_type: string;
  target_value: string;
  current_progress: number;
  status: string;
  priority: string;
  start_date: string;
  target_date: string;
  is_frozen: boolean;
}

export const useGoalManagement = (selectedTeamMember: string) => {
  const [personalGoals, setPersonalGoals] = useState<PerformanceGoal[]>([]);
  const [selectedMemberGoals, setSelectedMemberGoals] = useState<PerformanceGoal[]>([]);
  const [isLoadingMemberGoals, setIsLoadingMemberGoals] = useState(false);
  const { activeYear } = usePerformanceYear();

  const loadPersonalGoals = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return;
      }

      const { data: goals, error } = await supabase
        .from('performance_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('year', activeYear);

      if (error) {
        console.error("Error loading personal goals:", error);
        return;
      }

      const sortedGoals = (goals || []).sort((a, b) => a.goal_title.localeCompare(b.goal_title));
      setPersonalGoals(sortedGoals);
    } catch (error) {
      console.error("Error loading personal goals:", error);
    }
  };

  const loadSelectedMemberGoals = async () => {
    if (!selectedTeamMember) return;
    
    setIsLoadingMemberGoals(true);
    try {
      const { data: goals, error } = await supabase
        .from('performance_goals')
        .select('*')
        .eq('user_id', selectedTeamMember)
        .eq('year', activeYear);

      if (error) {
        console.error("Error loading member goals:", error);
        return;
      }

      const sortedGoals = (goals || []).sort((a, b) => a.goal_title.localeCompare(b.goal_title));
      setSelectedMemberGoals(sortedGoals);
    } catch (error) {
      console.error("Error loading member goals:", error);
    } finally {
      setIsLoadingMemberGoals(false);
    }
  };

  useEffect(() => {
    loadPersonalGoals();
  }, [activeYear]);

  useEffect(() => {
    if (selectedTeamMember) {
      loadSelectedMemberGoals();
    } else {
      setSelectedMemberGoals([]);
    }
  }, [selectedTeamMember, activeYear]);

  return {
    personalGoals,
    selectedMemberGoals,
    isLoadingMemberGoals,
    loadPersonalGoals,
    loadSelectedMemberGoals,
  };
};
